import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, ArrowLeft, Upload, FileCheck2, CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["", "— ignora —"], ["company_name", "Ragione sociale / Cognome"], ["sede_nome", "Sede / Nome"], ["category", "Tipologia"], ["vat_number", "Partita IVA"],
  ["tax_code", "Codice fiscale"], ["address", "Indirizzo"], ["postal_code", "CAP"], ["city", "Comune"],
  ["province", "Provincia"], ["region", "Regione"], ["country", "Nazione"], ["phone", "Telefono"], ["email", "Email"],
  ["website", "Sito"], ["primary_contact_name", "Referente"], ["primary_contact_role", "Ruolo referente"],
  ["source_list", "Fonte elenco"], ["status", "Stato"], ["notes", "Note"],
];
const MODES = [["solo_nuovi", "Solo nuovi"], ["aggiorna_esistenti", "Aggiorna esistenti"], ["sincronizza", "Sincronizza"]];
const DUP_FIELDS = [["company_name", "Ragione sociale"], ["sede_nome", "Sede/Nome"], ["vat_number", "P.IVA"], ["tax_code", "CF"], ["address", "Indirizzo"], ["city", "Comune"], ["province", "Prov."], ["phone", "Telefono"], ["email", "Email"], ["primary_contact_name", "Referente"]];

function Rec({ rec, other }) {
  return (
    <div className="space-y-0.5">
      {DUP_FIELDS.map(([k, l]) => {
        const v = rec[k] || "";
        const differ = (v || "").trim().toLowerCase() !== ((other[k] || "").trim().toLowerCase());
        return (
          <div key={k} className="flex gap-1">
            <span className="text-muted-foreground w-24 shrink-0">{l}</span>
            <span className={`truncate ${differ && v ? "text-amber-500 font-medium" : ""}`}>{v || "—"}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientiImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("solo_nuovi");
  const [resolutions, setResolutions] = useState({});
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/clienti-imports").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  useEffect(() => { loadHistory(); }, []);

  const doPreview = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/clienti/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setMapping(data.suggested_mapping || {}); setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "File non leggibile"); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/clienti/import/commit?mode=${mode}&mapping=${encodeURIComponent(JSON.stringify(mapping))}&resolutions=${encodeURIComponent(JSON.stringify(resolutions))}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data); setStep(3); loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore importazione"); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5 max-w-4xl mx-auto" data-testid="clienti-import-page">
      <div className="flex items-center justify-between"><h1 className="text-2xl font-heading font-bold">Importa Clienti</h1><Button variant="ghost" size="sm" onClick={() => navigate("/clienti")}><ArrowLeft className="h-4 w-4 mr-1.5" /> Clienti</Button></div>

      {step === 1 && (
        <Card className="rounded border-border p-8 text-center" data-testid="clienti-import-step-upload">
          <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">Carica un file CSV, XLS o XLSX con l'elenco dei Clienti.</p>
          <input id="cli-imp-file" type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); doPreview(f); } }} data-testid="clienti-import-file-input" />
          <Button onClick={() => document.getElementById("cli-imp-file").click()} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Scegli file</Button>
        </Card>
      )}

      {step === 1 && history.length > 0 && (
        <Card className="rounded border-border overflow-x-auto" data-testid="clienti-import-history">
          <div className="px-4 pt-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground"><History className="h-3.5 w-3.5" /> Storico importazioni</div>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent border-border">
              <TableHead className="text-xs uppercase">File</TableHead><TableHead className="text-xs uppercase">Data</TableHead><TableHead className="text-xs uppercase">Modalità</TableHead><TableHead className="text-xs uppercase">Utente</TableHead>
              <TableHead className="text-xs uppercase text-right">Righe</TableHead><TableHead className="text-xs uppercase text-right">Nuovi</TableHead><TableHead className="text-xs uppercase text-right">Aggiornati</TableHead><TableHead className="text-xs uppercase text-right">Ignorati</TableHead><TableHead className="text-xs uppercase text-right">Duplicati</TableHead><TableHead className="text-xs uppercase text-right">Errori</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} className="border-border">
                  <TableCell className="text-sm font-medium max-w-[180px] truncate" title={h.filename}>{h.filename}</TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{h.created_at ? new Date(h.created_at).toLocaleString("it-IT") : "—"}</TableCell>
                  <TableCell className="text-xs">{({ solo_nuovi: "Solo nuovi", aggiorna_esistenti: "Aggiorna", sincronizza: "Sincronizza" })[h.import_mode] || h.import_mode}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{h.uploaded_by || "—"}</TableCell>
                  <TableCell className="text-right">{h.total_rows}</TableCell>
                  <TableCell className="text-right text-emerald-500">{h.created_count}</TableCell>
                  <TableCell className="text-right text-blue-500">{h.updated_count}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{h.skipped_count}</TableCell>
                  <TableCell className="text-right text-amber-500">{h.duplicate_count}</TableCell>
                  <TableCell className="text-right text-red-500">{h.error_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {step === 2 && preview && (
        <div className="space-y-4" data-testid="clienti-import-step-review">
          <Card className="rounded border-border p-4">
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <span><b>{preview.rows_total}</b> righe</span>
              <span className="text-emerald-500">Nuovi: <b>{preview.counts.nuovo}</b></span>
              <span className="text-amber-500">Da aggiornare (certi): <b>{preview.counts.certa}</b></span>
              <span className="text-red-500">Possibili duplicati: <b>{preview.counts.possibile}</b></span>
              {preview.address_missing > 0 && <span className="text-amber-600 font-medium" data-testid="clienti-import-address-warning">⚠ {preview.address_missing} righe senza indirizzo/comune (non geolocalizzabili)</span>}
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs uppercase text-muted-foreground">Modalità</Label>
                <Select value={mode} onValueChange={setMode}><SelectTrigger className="w-48" data-testid="clienti-import-mode"><SelectValue /></SelectTrigger><SelectContent>{MODES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </Card>

          <Card className="rounded border-border p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Mappatura colonne</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <span className="text-sm w-2/5 truncate" title={h}>{h}</span>
                  <Select value={mapping[h] || ""} onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v === "__none__" ? "" : v }))}>
                    <SelectTrigger className="flex-1" data-testid={`clienti-map-${h}`}><SelectValue placeholder="— ignora —" /></SelectTrigger>
                    <SelectContent>{FIELDS.map(([k, l]) => <SelectItem key={k || "__none__"} value={k || "__none__"}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </Card>

          {preview.preview.some((r) => r.classification === "possibile") && (
            <Card className="rounded border-amber-500/40 p-4" data-testid="clienti-import-duplicates">
              <div className="text-xs uppercase tracking-widest text-amber-500 mb-1">Possibili duplicati — confronto affiancato</div>
              <p className="text-[11px] text-muted-foreground mb-3">Nessuna unione automatica: decidi tu per ciascun caso.</p>
              <div className="space-y-3">
                {preview.preview.filter((r) => r.classification === "possibile").map((r) => (
                  <div key={r.row_index} className="rounded border border-border p-3" data-testid={`clienti-dup-row-${r.row_index}`}>
                    <div className="grid sm:grid-cols-2 gap-4 text-xs">
                      <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Dal file</div><Rec rec={r.data} other={r.match_data || {}} /></div>
                      <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Record esistente</div><Rec rec={r.match_data || {}} other={r.data} /></div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Azione</Label>
                      <Select value={resolutions[r.row_index] || "ignore"} onValueChange={(v) => setResolutions((m) => ({ ...m, [r.row_index]: v }))}>
                        <SelectTrigger className="w-64" data-testid={`clienti-dup-action-${r.row_index}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ignore">Ignora (non importare)</SelectItem>
                          <SelectItem value="update">Aggiorna record esistente</SelectItem>
                          <SelectItem value="create">Crea nuovo comunque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="rounded border-border overflow-x-auto">
            <Table><TableHeader><TableRow className="hover:bg-transparent border-border"><TableHead className="text-xs uppercase">Ragione sociale</TableHead><TableHead className="text-xs uppercase">Comune</TableHead><TableHead className="text-xs uppercase">P.IVA</TableHead><TableHead className="text-xs uppercase">Esito</TableHead></TableRow></TableHeader>
              <TableBody>{preview.preview.slice(0, 30).map((r, i) => (
                <TableRow key={i} className="border-border"><TableCell className="text-sm">{r.data.company_name}</TableCell><TableCell className="text-sm text-muted-foreground">{r.data.city}</TableCell><TableCell className="text-sm text-muted-foreground">{r.data.vat_number}</TableCell>
                  <TableCell><span className={`text-xs font-semibold ${r.classification === "nuovo" ? "text-emerald-500" : r.classification === "certa" ? "text-amber-500" : "text-red-500"}`}>{r.classification === "nuovo" ? "Nuovo" : r.classification === "certa" ? "Duplicato certo" : "Possibile duplicato"}</span></TableCell>
                </TableRow>))}
              </TableBody></Table>
          </Card>

          <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)}>Indietro</Button><Button onClick={doCommit} disabled={busy} data-testid="clienti-import-commit-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck2 className="h-4 w-4 mr-2" />} Conferma importazione</Button></div>
        </div>
      )}

      {step === 3 && report && (
        <Card className="rounded border-border p-8 text-center" data-testid="clienti-import-step-report">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="font-heading font-bold text-lg mb-4">Importazione completata</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-xl mx-auto">
            {[["Creati", report.created, "text-emerald-500"], ["Aggiornati", report.updated, "text-blue-500"], ["Ignorati", report.skipped, ""], ["Poss. duplicati", report.duplicate, "text-red-500"], ["Errori", report.errors, "text-red-500"]].map(([l, v, cc]) => (
              <div key={l} className="rounded border border-border p-3"><div className={`font-heading font-bold text-2xl ${cc}`}>{v}</div><div className="text-[10px] uppercase text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-6"><Button variant="outline" onClick={() => { setStep(1); setFile(null); setPreview(null); setReport(null); setResolutions({}); }}>Nuova importazione</Button><Button onClick={() => navigate("/clienti")}>Vai ai Clienti</Button></div>
        </Card>
      )}
    </div>
  );
}
